/**
 * MainLayout - Primary app layout with map, HUD, navigation, and chat
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useChatStore, useAirQualityStore, useNavigationStore, useLocationStore, useRunPlannerStore, useMapStore, addEnvironmentalAlert } from '@/stores';
import { useRunningStore } from '@/stores/useRunningStore';
import { ActivityTypePicker } from '@/running/components/ActivityTypePicker';
import { LiveRecordingScreen } from '@/running/components/LiveRecordingScreen';
import { PostActivitySummary } from '@/running/components/PostActivitySummary';
import { ExposureDashboard } from '@/running/components/ExposureDashboard';
import { BestTimeToRun } from '@/running/components/BestTimeToRun';
import type { RecordingResult } from '@/types/running';
import { MapContainer } from '@/components/map';
import { HudContainer } from '@/components/hud';
import { ChatSidebar } from '@/components/chat';
import { SearchBar, RoutePreview, RoutePrioritySelector, DemoMap, MapControls, WazeStyleNav } from '@/components/navigation';
import { RunPlanner } from '@/components/runplanner';
import { LoginModal } from '@/auth/components/LoginModal';
import { CreateReport } from '@/social/components/CreateReport';
import { TrendingScents, ScentAlerts } from '@/components/social';
import { RunningHub } from '@/running/pages/RunningHub';
import { ProfileButton } from './ProfileButton';
import { cn } from '@/utils/cn';
import { useAirQualityPolling } from '@/hooks/useAirQualityPolling';
import { zoomIn, zoomOut, recenterToUser, resetNorth } from '@/utils/mapInstance';

// Check if Mapbox token is configured
const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
const hasMapboxToken = Boolean(MAPBOX_TOKEN && MAPBOX_TOKEN !== 'YOUR_MAPBOX_TOKEN' && MAPBOX_TOKEN.startsWith('pk.'));

// Debug: log token status (remove in production)
if (typeof window !== 'undefined') {
  console.log('Mapbox token status:', hasMapboxToken ? 'Valid' : 'Missing/Invalid');
}

export function MainLayout() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [mapHeading, setMapHeading] = useState(0);

  // Login modal and Create Report state
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [createReportOpen, setCreateReportOpen] = useState(false);
  const [trendingOpen, setTrendingOpen] = useState(false);
  const [runningHubOpen, setRunningHubOpen] = useState(false);
  const [runningHubView, setRunningHubView] = useState<'feed' | 'explore' | 'clubs' | 'challenges' | 'profile'>('feed');
  const [exposureDashOpen, setExposureDashOpen] = useState(false);
  const [bestTimeOpen, setBestTimeOpen] = useState(false);

  // Activity recording state
  const [showActivityPicker, setShowActivityPicker] = useState(false);
  const [showLiveRecording, setShowLiveRecording] = useState(false);
  const [showPostSummary, setShowPostSummary] = useState(false);
  const [completedResult, setCompletedResult] = useState<RecordingResult | null>(null);
  const startRecording = useRunningStore((s) => s.startRecording);
  const stopRecording = useRunningStore((s) => s.stopRecording);
  const isRecording = useRunningStore((s) => s.isRecording);

  // Route preview state
  const [previewRoute, setPreviewRoute] = useState<{
    destinationName: string;
    totalDistance: number;
    totalDuration: number;
  } | null>(null);

  // Chat state
  const chatOpen = useChatStore((state) => state.isOpen);
  const openSidebar = useChatStore((state) => state.openSidebar);
  const closeSidebar = useChatStore((state) => state.closeSidebar);

  // Navigation state
  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const selectedDestination = useNavigationStore((state) => state.selectedDestination);
  const prioritySelected = useNavigationStore((state) => state.prioritySelected);
  const setRoutePriority = useNavigationStore((state) => state.setRoutePriority);

  // Run Planner state
  const openRunPlanner = useRunPlannerStore((state) => state.openPlanner);
  const runPlannerOpen = useRunPlannerStore((state) => state.isOpen);

  // Air quality polling - fetches real-time data from APIs
  useAirQualityPolling({
    enabled: true,
    interval: 5 * 60 * 1000, // Poll every 5 minutes
  });

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect route preview mode from sessionStorage
  useEffect(() => {
    const isPreview = sessionStorage.getItem('blurp_preview_mode') === 'true';
    if (isPreview) {
      try {
        const routeData = JSON.parse(sessionStorage.getItem('blurp_pending_route') || '');
        if (routeData) {
          setPreviewRoute({
            destinationName: routeData.destinationName || 'Destination',
            totalDistance: routeData.totalDistance || 0,
            totalDuration: routeData.totalDuration || 0,
          });
        }
      } catch {
        setPreviewRoute(null);
      }
    } else {
      setPreviewRoute(null);
    }
  }, [isNavigating]);

  // Start walk from preview — also auto-start recording with air metrics
  const handleStartWalk = useCallback(() => {
    try {
      const routeData = JSON.parse(sessionStorage.getItem('blurp_pending_route') || '');
      if (routeData) {
        sessionStorage.removeItem('blurp_preview_mode');
        sessionStorage.removeItem('blurp_pending_route');
        setPreviewRoute(null);
        // Start activity recording (GPS + AQI sampling) with full recording UI
        startRecording('walk');
        setShowLiveRecording(true);
        // Start turn-by-turn navigation
        useNavigationStore.getState().startNavigation(routeData);
      }
    } catch {}
  }, [startRecording]);

  // Go back to route planning from preview
  const handleBackToPlanning = useCallback(() => {
    sessionStorage.removeItem('blurp_preview_mode');
    sessionStorage.removeItem('blurp_pending_route');
    setPreviewRoute(null);
    navigate('/plan-route');
  }, [navigate]);

  // Auto-stop recording when navigation ends (arrival or manual stop)
  const wasNavigatingRef = useRef(false);
  useEffect(() => {
    if (isNavigating) {
      wasNavigatingRef.current = true;
    } else if (wasNavigatingRef.current && isRecording) {
      // Navigation just ended while recording — stop recording and show summary
      wasNavigatingRef.current = false;
      const result = stopRecording();
      setShowLiveRecording(false);
      if (result) {
        setCompletedResult(result);
        setShowPostSummary(true);
      }
    }
  }, [isNavigating, isRecording, stopRecording]);

  // Listen for chat actions (e.g., open Best Time page from AI chat)
  useEffect(() => {
    const handler = (e: Event) => {
      const action = (e as CustomEvent).detail;
      if (action === 'open-best-time') setBestTimeOpen(true);
      if (action === 'open-exposure') setExposureDashOpen(true);
    };
    window.addEventListener('blurp-chat-action', handler);
    return () => window.removeEventListener('blurp-chat-action', handler);
  }, []);

  // Initialize - request real geolocation and set up data
  useEffect(() => {
    // Clear any stale cached position on app start and set to Malaysia default
    useLocationStore.getState().clearHistory();
    useLocationStore.getState().setPosition(
      { lat: 3.139, lng: 101.6869 }, // Default: Kuala Lumpur, Malaysia
      1000, // Low accuracy to indicate it's a default
      0
    );

    // Request real user location
    if ('geolocation' in navigator) {
      // First try with high accuracy (GPS)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Got real location - update immediately
          const { latitude, longitude } = position.coords;
          console.log('Got real GPS location:', latitude, longitude);
          useLocationStore.getState().setPosition(
            { lat: latitude, lng: longitude },
            position.coords.accuracy,
            position.coords.heading ?? 0
          );
          // Also fly the map to the user's location
          useMapStore.getState().flyTo({ lat: latitude, lng: longitude }, 15);
        },
        (error) => {
          console.warn('High accuracy geolocation failed:', error.message);
          // Try again without high accuracy requirement (faster, uses cell towers/wifi)
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const { latitude, longitude } = position.coords;
              console.log('Got location (low accuracy):', latitude, longitude);
              useLocationStore.getState().setPosition(
                { lat: latitude, lng: longitude },
                position.coords.accuracy,
                position.coords.heading ?? 0
              );
              useMapStore.getState().flyTo({ lat: latitude, lng: longitude }, 15);
            },
            (fallbackError) => {
              console.warn('All geolocation failed, using Malaysia default:', fallbackError.message);
              // Keep the Kuala Lumpur default already set
            },
            { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
          );
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 } // maximumAge: 0 forces fresh location
      );

      // Watch position for continuous updates
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          // Update position, but heading will come from device orientation
          const currentHeading = useLocationStore.getState().heading;
          useLocationStore.getState().setPosition(
            { lat: position.coords.latitude, lng: position.coords.longitude },
            position.coords.accuracy,
            currentHeading ?? position.coords.heading ?? 0,
            position.coords.speed ?? undefined
          );
        },
        () => {}, // Ignore watch errors silently
        { enableHighAccuracy: true, maximumAge: 30000 }
      );

      // Set up device orientation for compass heading (works on mobile)
      const handleOrientation = (event: DeviceOrientationEvent) => {
        if (event.alpha !== null) {
          // alpha gives the compass heading (0-360 degrees)
          // For iOS, we need to use webkitCompassHeading if available
          const heading = (event as any).webkitCompassHeading ?? (360 - event.alpha);
          const currentState = useLocationStore.getState();

          // Update heading without changing position
          if (currentState.currentPosition) {
            currentState.setPosition(
              currentState.currentPosition,
              currentState.accuracy ?? 10,
              heading,
              currentState.speed ?? undefined
            );
          }
        }
      };

      // Request permission for iOS 13+
      if (typeof (DeviceOrientationEvent as any).requestPermission === 'function') {
        (DeviceOrientationEvent as any).requestPermission()
          .then((permissionState: string) => {
            if (permissionState === 'granted') {
              window.addEventListener('deviceorientation', handleOrientation);
            }
          })
          .catch(console.error);
      } else {
        // Non-iOS or older iOS - just add the listener
        window.addEventListener('deviceorientation', handleOrientation);
      }

      return () => {
        navigator.geolocation.clearWatch(watchId);
        window.removeEventListener('deviceorientation', handleOrientation);
      };
    } else {
      console.warn('Geolocation not available, using Malaysia default');
      // Keep the Malaysia default already set above
    }
  }, []);

  // Initialize demo mode and initial data
  useEffect(() => {
    // Set initial demo air quality data (will be replaced by real API data)
    useAirQualityStore.getState().setAirQuality({
      pm25: { value: 12, unit: 'µg/m³', status: 'good', trend: 'stable', timestamp: Date.now() },
      ozone: { value: 34, unit: 'ppb', status: 'moderate', trend: 'falling', timestamp: Date.now() },
      co2: { value: 410, unit: 'ppm', status: 'good', trend: 'stable', timestamp: Date.now() },
      aqi: 42,
      dominantPollutant: 'pm25',
      timestamp: Date.now(),
      location: { lat: 3.139, lng: 101.6869 },
    });

    // Set demo mode based on Mapbox token availability
    if (hasMapboxToken) {
      useNavigationStore.getState().setDemoMode(false);
    } else {
      useNavigationStore.getState().setDemoMode(true);
    }

    // Add initial system alert after a short delay
    const timer = setTimeout(() => {
      addEnvironmentalAlert(
        'info',
        'Environmental Intelligence',
        'Blurp.ie is now monitoring air quality in your area.',
        { dismissible: true }
      );
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Swipe gesture handling for mobile
  const [touchStart, setTouchStart] = useState<number | null>(null);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (touchStart === null) return;

      const touchEnd = e.changedTouches[0].clientX;
      const diff = touchStart - touchEnd;

      // Swipe left to open chat (if swiping from right edge)
      if (diff > 50 && touchStart > window.innerWidth - 50) {
        openSidebar();
      }
      // Swipe right to close chat
      if (diff < -50 && chatOpen) {
        closeSidebar();
      }

      setTouchStart(null);
    },
    [touchStart, chatOpen, openSidebar, closeSidebar]
  );

  // Map control handlers - now connected to real Mapbox map
  const handleZoomIn = useCallback(() => {
    zoomIn();
  }, []);

  const handleZoomOut = useCallback(() => {
    zoomOut();
  }, []);

  const handleRecenter = useCallback(() => {
    const pos = useLocationStore.getState().currentPosition;
    if (pos) {
      recenterToUser(pos.lat, pos.lng);
    }
  }, []);

  const handleCompassClick = useCallback(() => {
    resetNorth();
    setMapHeading(0);
  }, []);

  // Use real map if Mapbox token is available, otherwise show demo
  // Force real map when token is valid, regardless of isDemoMode state
  const showDemoMap = !hasMapboxToken;

  return (
    <div
      className="relative w-full h-screen overflow-hidden bg-bg-deep"
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      {/* Layer 1: Full-screen Map */}
      <div className="absolute inset-0 z-0">
        {showDemoMap ? <DemoMap /> : <MapContainer />}
      </div>

      {/* Layer 2: Waze-style Navigation UI (when navigating) */}
      <AnimatePresence>
        {isNavigating && <WazeStyleNav />}
      </AnimatePresence>

      {/* Layer 3: Search & Route Preview (when not navigating) */}
      {!isNavigating && (
        <div className="absolute top-0 left-0 right-0 z-20 p-4 pointer-events-none">
          <div className="max-w-lg mx-auto space-y-3">
            {/* Search Bar */}
            <div className="pointer-events-auto">
              <SearchBar />
            </div>

            {/* Priority Selector (when destination selected but priority not chosen) */}
            <AnimatePresence mode="wait">
              {selectedDestination && !prioritySelected && (
                <div className="pointer-events-auto">
                  <RoutePrioritySelector onSelect={setRoutePriority} />
                </div>
              )}
            </AnimatePresence>

            {/* Route Preview (when destination selected AND priority chosen) */}
            <AnimatePresence>
              {selectedDestination && prioritySelected && (
                <div className="pointer-events-auto">
                  <RoutePreview />
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Layer 4: Map Controls (zoom, compass, recenter) - right side */}
      <div className={cn(
        'absolute z-20 pointer-events-auto right-4',
        isNavigating ? 'bottom-40' : 'bottom-4'
      )}>
        <MapControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onRecenter={handleRecenter}
          onCompassClick={handleCompassClick}
          heading={mapHeading}
        />
      </div>

      {/* Layer 5: HUD Overlay (only show when not navigating for cleaner UI) */}
      {!isNavigating && (
        <div className="absolute inset-0 z-10 pointer-events-none">
          <HudContainer isMobile={isMobile} />
        </div>
      )}

      {/* Layer 6: Chat Sidebar */}
      <ChatSidebar
        isOpen={chatOpen}
        onClose={closeSidebar}
        isMobile={isMobile}
      />

      {/* Layer 7: Run Planner Modal */}
      <RunPlanner />

      {/* Layer 8: Login Modal */}
      <LoginModal
        isOpen={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
      />

      {/* Layer 9: Create Report Form */}
      <CreateReport
        isOpen={createReportOpen}
        onClose={() => setCreateReportOpen(false)}
      />

      {/* Layer 10: Trending Scents Panel */}
      <TrendingScents
        isOpen={trendingOpen}
        onClose={() => setTrendingOpen(false)}
      />

      {/* Layer 11: Running Community Hub */}
      <RunningHub
        isOpen={runningHubOpen}
        onClose={() => setRunningHubOpen(false)}
        initialView={runningHubView}
      />

      {/* Layer 12: Exposure Dashboard */}
      <ExposureDashboard
        isOpen={exposureDashOpen}
        onClose={() => setExposureDashOpen(false)}
      />

      {/* Layer 13: Best Time to Run */}
      <BestTimeToRun
        isOpen={bestTimeOpen}
        onClose={() => setBestTimeOpen(false)}
      />

      {/* Layer 14: Scent Alerts (always visible) */}
      {!isNavigating && <ScentAlerts />}

      {/* Top-right: Action buttons (clean horizontal row, away from map controls) */}
      {!chatOpen && !runPlannerOpen && !isNavigating && !createReportOpen && (
        <div className="fixed top-20 right-4 z-30 flex flex-col gap-2">
          {/* Report Scents Button */}
          <ProfileButton
            onLoginClick={() => setLoginModalOpen(true)}
            onSocialClick={() => setCreateReportOpen(true)}
          />

          {/* Plan Run Button */}
          <button
            onClick={openRunPlanner}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'bg-slate-900/80 backdrop-blur-md',
              'border border-slate-700/50',
              'text-emerald-400 text-sm font-medium',
              'shadow-lg',
              'hover:bg-slate-800/80 transition-colors',
              'active:scale-95'
            )}
          >
            <RunIcon className="w-4 h-4" />
            <span>Plan Run</span>
          </button>

          {/* Trending Scents Button */}
          <button
            onClick={() => setTrendingOpen(true)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'bg-slate-900/80 backdrop-blur-md',
              'border border-slate-700/50',
              'text-fuchsia-400 text-sm font-medium',
              'shadow-lg',
              'hover:bg-slate-800/80 transition-colors',
              'active:scale-95'
            )}
          >
            <TrendingIcon className="w-4 h-4" />
            <span>Trending</span>
          </button>

          {/* Best Time to Run Button */}
          <button
            onClick={() => setBestTimeOpen(true)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'bg-slate-900/80 backdrop-blur-md',
              'border border-slate-700/50',
              'text-amber-400 text-sm font-medium',
              'shadow-lg',
              'hover:bg-slate-800/80 transition-colors',
              'active:scale-95'
            )}
          >
            <ClockIcon className="w-4 h-4" />
            <span>Best Time</span>
          </button>

          {/* Exposure Dashboard Button */}
          <button
            onClick={() => setExposureDashOpen(true)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'bg-slate-900/80 backdrop-blur-md',
              'border border-slate-700/50',
              'text-purple-400 text-sm font-medium',
              'shadow-lg',
              'hover:bg-slate-800/80 transition-colors',
              'active:scale-95'
            )}
          >
            <LungsIcon className="w-4 h-4" />
            <span>Exposure</span>
          </button>

          {/* Profile Button */}
          <button
            onClick={() => { setRunningHubView('profile'); setRunningHubOpen(true); }}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'bg-slate-900/80 backdrop-blur-md',
              'border border-slate-700/50',
              'text-cyan-400 text-sm font-medium',
              'shadow-lg',
              'hover:bg-slate-800/80 transition-colors',
              'active:scale-95'
            )}
          >
            <ProfileIcon className="w-4 h-4" />
            <span>Profile</span>
          </button>

          {/* Recording Button */}
          <button
            onClick={() => setShowActivityPicker(true)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg',
              'bg-red-600/90 backdrop-blur-md',
              'border border-red-500/50',
              'text-white text-sm font-bold',
              'shadow-lg shadow-red-500/20',
              'hover:bg-red-500/90 transition-all',
              'active:scale-95'
            )}
          >
            <span>RECORDING</span>
          </button>
        </div>
      )}

      {/* Route Preview Bar — floating at bottom when previewing a route */}
      <AnimatePresence>
        {previewRoute && !isNavigating && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-40"
          >
            <div className="bg-slate-900/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
              <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
                {/* Route info */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-semibold text-base">{previewRoute.destinationName}</p>
                    <p className="text-gray-400 text-sm">
                      {(previewRoute.totalDistance / 1000).toFixed(1)} km · {
                        previewRoute.totalDuration >= 3600
                          ? `${Math.floor(previewRoute.totalDuration / 3600)} hr ${Math.round((previewRoute.totalDuration % 3600) / 60)} min`
                          : `${Math.round(previewRoute.totalDuration / 60)} min`
                      } walk
                    </p>
                  </div>
                  <button
                    onClick={handleBackToPlanning}
                    className="px-3 py-1.5 rounded-lg bg-white/10 text-gray-400 hover:text-white hover:bg-white/20 transition-all text-sm"
                  >
                    Edit
                  </button>
                </div>

                {/* Start Walk button */}
                <button
                  onClick={handleStartWalk}
                  className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 rounded-xl font-bold text-black text-lg transition-all flex items-center justify-center gap-3 active:scale-[0.98]"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <circle cx="12" cy="5" r="2" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10 22l2-7 3 3 3-8M7 12l3-3" />
                  </svg>
                  Start Walk
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* Activity Type Picker */}
      <ActivityTypePicker
        isOpen={showActivityPicker}
        onClose={() => setShowActivityPicker(false)}
        onSelect={(type) => {
          setShowActivityPicker(false);
          startRecording(type);
          setShowLiveRecording(true);
        }}
      />

      {/* Live Recording Overlay */}
      <LiveRecordingScreen
        isOpen={showLiveRecording}
        onFinish={(result) => {
          setShowLiveRecording(false);
          setCompletedResult(result);
          setShowPostSummary(true);
        }}
        onDiscard={() => {
          setShowLiveRecording(false);
          setCompletedResult(null);
        }}
      />

      {/* Post-Activity Summary */}
      <PostActivitySummary
        isOpen={showPostSummary}
        result={completedResult}
        onClose={() => {
          setShowPostSummary(false);
          setCompletedResult(null);
        }}
      />

      {/* Navigation mode: Minimal back button */}
      {isNavigating && (
        <button
          className={cn(
            'fixed top-4 left-4 z-30',
            'w-10 h-10 rounded-xl',
            'bg-slate-900/80 backdrop-blur-md',
            'border border-slate-700/50',
            'flex items-center justify-center',
            'text-slate-400',
            'hover:bg-slate-800/80 transition-colors'
          )}
          title="Menu"
        >
          <AlertIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}


// Alert icon (hamburger with notification)
function AlertIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </svg>
  );
}

// Run icon (running person)
function RunIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="17" cy="4" r="2" />
      <path d="M15.59 13.51l-2.22-2.38-4.06 4.06L7 13l-4 4 1.41 1.41L7 15.83l2.31 2.18 5.69-5.5 1.59 1.59V21h2v-8.49z" />
      <path d="M9.5 5.5c1.1 1 1.96 1.78 2.63 2.5H6v2h6.1L15.17 7c-.59-1.02-1.24-1.79-1.96-2.5H9.5z" />
    </svg>
  );
}

// Trending icon
function TrendingIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}

// Clock icon (for best time to run)
function ClockIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

// Lungs icon (for exposure dashboard)
function LungsIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M12 2v8" />
      <path d="M6.8 14.4A4 4 0 0 0 4 18c0 2.2 1.8 4 4 4h1a1 1 0 0 0 1-1v-7" />
      <path d="M17.2 14.4A4 4 0 0 1 20 18c0 2.2-1.8 4-4 4h-1a1 1 0 0 1-1-1v-7" />
      <path d="M8 10a4 4 0 0 1 4-4 4 4 0 0 1 4 4" />
    </svg>
  );
}

// Profile icon
function ProfileIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  );
}

export default MainLayout;
